import { forwardRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { discussionApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import UntrustedMarkdown from "./UntrustedMarkdown";
import { discussionPath, formatDay, whoName } from "./lib/format";

/** "Latest of 3 posts — sketcher on Oct 10, 2026". */
function latestLine(count, post) {
  const lead = count === 1 ? "The only post so far" : `Latest of ${count} posts`;
  const day = formatDay(post.created_at);
  return `${lead} — ${whoName(post.author)}${day ? ` on ${day}` : ""}`;
}

/**
 * The company's discussion (spec walkthrough 6): its title, linked; the
 * latest post (anyone in the household may write one, agents included, so
 * it renders untrusted — D8); and the decision pinned in it, if any.
 * Read through the discussions API as it is — reading here doesn't mark
 * the thread read. If it can't be read, the link still works.
 */
const DiscussionPanel = forwardRef(function DiscussionPanel({ discussion }, headingRef) {
  const [thread, setThread] = useState(null);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    setError(null);
    discussionApi.get(discussion.id).then(
      ({ data }) => {
        if (live) setThread(data);
      },
      (err) => {
        if (live) setError(err);
      },
    );
    return () => {
      live = false;
    };
  }, [discussion.id, tick]);

  const posts = Array.isArray(thread?.posts) ? thread.posts : [];
  const latest = posts[posts.length - 1];
  const count = thread?.post_count ?? posts.length;
  const decidedDay = formatDay(thread?.decided_at);

  return (
    <section aria-labelledby="investing-discussion" className="rounded-lg border bg-card p-4">
      <h2 id="investing-discussion" ref={headingRef} tabIndex={-1} className="mt-0 mb-1 text-lg font-semibold outline-none">
        Discussion
      </h2>
      <p className="mt-0 mb-2">
        <Link
          to={discussionPath(discussion.id)}
          className="inline-flex min-h-11 items-center font-medium underline underline-offset-2 wrap-anywhere md:min-h-0"
        >
          {discussion.title}
        </Link>
      </p>
      {error ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span>
            Couldn&apos;t load the discussion ({apiErrorMessage(error, "error")}) — the link above still
            works.
          </span>{" "}
          <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={() => setTick((t) => t + 1)}>
            Retry
          </Button>
        </div>
      ) : !thread ? (
        <p className="m-0 text-sm text-muted-foreground">Loading the discussion…</p>
      ) : (
        <>
          <p className="m-0 text-sm">
            {thread.decision ? (
              <>
                <span className="font-semibold">
                  Decision pinned by {whoName(thread.decided_by)}
                  {decidedDay ? ` on ${decidedDay}` : ""}:
                </span>{" "}
                <span className="wrap-anywhere">{thread.decision}</span>
              </>
            ) : (
              <span className="text-muted-foreground">
                No decision pinned yet — pin one in the discussion once it&apos;s settled.
              </span>
            )}
          </p>
          {latest && (
            <div className="mt-3 border-t border-border pt-3">
              <p className="mt-0 mb-1 text-sm text-muted-foreground">{latestLine(count, latest)}</p>
              {latest.hidden || latest.body === null ? (
                <p className="m-0 text-sm">Hidden from you until the panel&apos;s answers open.</p>
              ) : (
                <UntrustedMarkdown body={latest.body || ""} />
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
});

DiscussionPanel.propTypes = {
  /** The company's link: `{id, title}`. */
  discussion: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }).isRequired,
};

export default DiscussionPanel;
